/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useTranslation } from 'react-i18next';

import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { FormControl, FormHelperText, List, ListItem, Typography } from '@mui/material';
import { FieldTemplateProps } from '@rjsf/utils';

import { WarningIcon } from '../../../../../../components/svg-icons/WarningIcon';
import { PostgresqlContext } from '../../../../../../constants/postgresqlContext';
import { Namespaces } from '../../../../../../i18n';
import { ContextWarnings } from '../ContextWarning/ContextWarnings';
import { CustomFieldTemplateStyles } from './CustomFieldTemplate.styles';

// eslint-disable-next-line
export const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const { id, children, displayLabel, rawErrors = [], rawHelp, rawDescription, uiSchema, schema } = props;
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = CustomFieldTemplateStyles();
  const options = uiSchema?.['ui:options'];

  const checkboxField = schema.type === 'boolean';
  const textField = schema.type === 'integer' || schema.type === 'number' || schema.type === 'string';

  return (
    <div className={classes.fieldWrapper}>
      <FormControl fullWidth={true} error={!!rawErrors.length}>
        {children}
        {displayLabel && rawDescription ? (
          <Typography variant="caption" color="textSecondary">
            {rawDescription}
          </Typography>
        ) : null}
        {rawErrors.length > 0 && (
          <List classes={{ root: classes.root }}>
            {rawErrors.map((error, i: number) => {
              return (
                <ListItem key={i} classes={{ root: classes.listItem }}>
                  <FormHelperText id={id} classes={{ root: classes.helper }}>
                    {error}
                  </FormHelperText>
                </ListItem>
              );
            })}
          </List>
        )}
        {rawHelp && <FormHelperText id={id}>{rawHelp}</FormHelperText>}
      </FormControl>
      {(textField || checkboxField) && (
        <ContextWarnings
          variant={textField ? 'textField' : 'checkboxField'}
          warnings={[
            {
              IconComponent: WarningIcon,
              tooltip: t('details.parameters.warning.postmaster_warn'),
              isActive: options?.context === PostgresqlContext.Postmaster,
            },
            {
              IconComponent: SyncProblemIcon,
              tooltip: t('details.parameters.warning.sync_failed', {
                value: options?.actualValue,
              }),
              isActive: !!(options && 'actualValue' in options),
            },
          ]}
          minWarningsIndent={1}
        />
      )}
    </div>
  );
};
